// import differenceInYears from "date-fns/differenceInYears";
import { differenceInYears, isDate, parse } from "date-fns";
import * as yup from "yup";
import "yup-phone-lite";

import {
  buildQuoteSummarySchema,
  buildSchemaSelectCover,
} from "./selectCoverSchema.js";

import {
  LIFE_COVER_MAX_AGE,
  LIFE_COVER_MIN_AGE,
  ADDITIONAL_HOLDER_MIN_AGE,
  ADDITIONAL_HOLDER_MAX_AGE,
} from "./sharedConstValues.js";

// ----------------------------------------------------------------------
//** Your Details Personal */
//* Question options
const OPTION_SELECT = "Please select one option";

//* Title
export const TITLE = "Title is required";
//* First Name
export const MAX_NUMBER_CHARACTERS = 20;
export const FIRST_NAME_REQUIRED = "You must enter a first name";
export const FIRST_NAME_LENGTH = `First name must be less than ${MAX_NUMBER_CHARACTERS} characters`;
export const FIRST_NAME_REGEX = /^[a-zA-Z'\-\s`]+$/;
export const FIRST_NAME_NOT_VALID = "Please enter a valid first name";
//* Surname
export const SURNAME_REQUIRED = "You must enter a surname";
export const SURNAME_LENGTH = `Surname must be less than ${MAX_NUMBER_CHARACTERS} characters`;
export const SURNAME_REGEX = /^[a-zA-Z'\-\s`]+$/;
export const SURNAME_NOT_VALID = "Please enter a valid surname";
//* Gender
const GENDER = "You must select a gender";
//* DOB
export let DOB_MIN_AGE = `You must be at least ${LIFE_COVER_MIN_AGE.toString()} to apply for benefits`;
export let DOB_MAX_AGE = `The maximum age at which you can apply for Life cover is
  ${LIFE_COVER_MAX_AGE.toString()}`;

//* ADDITIONAL DOB
export let ADDITIONAL_DOB_MIN_AGE = `An Additional Policy Holder must be at least ${ADDITIONAL_HOLDER_MIN_AGE.toString()}.`;
export let ADDITIONAL_DOB_MAX_AGE = `The maximum age of an Additional Policy Holder is 
  ${ADDITIONAL_HOLDER_MAX_AGE.toString()}.`;

export const DOB_REQUIRED = "You must enter a date of birth";
export const INVALID_DATE = "Invalid date";

// const warning = (message) => () => Promise.resolve(message);

const residentQuestionValidation = (value) => {
  if (value === "no") {
    return false;
  } else {
    return true;
  }
};

export function parseDateString(value, originalValue) {
  const parsedDate = isDate(originalValue)
    ? originalValue
    : parse(originalValue, "dd/MM/yyyy", new Date());

  return parsedDate;
}

const buildPersonalDetailsSchema = (person) => {
  return {
    [`${person}_residentQuestion`]: yup
      .string()
      .oneOf(["yes", "no"])
      .required(OPTION_SELECT)
      .test("residentQuestion", "", residentQuestionValidation),
    [`${person}_title`]: yup.string().required(TITLE),
    [`${person}_firstName`]: yup
      .string()
      .trim()
      .max(MAX_NUMBER_CHARACTERS, FIRST_NAME_LENGTH)
      .required(FIRST_NAME_REQUIRED)
      .matches(FIRST_NAME_REGEX, FIRST_NAME_NOT_VALID),
    [`${person}_surname`]: yup
      .string()
      .trim()
      .max(MAX_NUMBER_CHARACTERS, SURNAME_LENGTH)
      .required(SURNAME_REQUIRED)
      .matches(SURNAME_REGEX, SURNAME_NOT_VALID),
    [`${person}_gender`]: yup
      .string()
      .required(GENDER)
      .oneOf(["male", "female"]),
    [`${person}_dob`]: yup
      .date()
      .nullable()
      .default(undefined)
      .transform(parseDateString)
      .typeError(INVALID_DATE)
      .required(DOB_REQUIRED)
      .test("dob_max", DOB_MAX_AGE, function (value) {
        return (
          differenceInYears(new Date(), new Date(value)) <= LIFE_COVER_MAX_AGE
        );
      })
      .test("dob_min", DOB_MIN_AGE, function (value) {
        return (
          differenceInYears(new Date(), new Date(value)) >= LIFE_COVER_MIN_AGE
        );
      }),
    [`additionalPolicyHolderTitle`]: yup
      .string()
      .when("$isAdditionalPolicyHolder", {
        is: true,
        then: yup.string().optional().required(TITLE),
        otherwise: yup.string().nullable().notRequired(),
      }),
    [`additionalPolicyHolderFirstName`]: yup
      .string()
      .when("$isAdditionalPolicyHolder", {
        is: true,
        then: yup
          .string()
          .trim()
          .optional()
          .max(MAX_NUMBER_CHARACTERS, FIRST_NAME_LENGTH)
          .required(FIRST_NAME_REQUIRED)
          .matches(FIRST_NAME_REGEX, FIRST_NAME_NOT_VALID),
        otherwise: yup.string().nullable().notRequired(),
      }),
    [`additionalPolicyHolderSurname`]: yup
      .string()
      .when("$isAdditionalPolicyHolder", {
        is: true,
        then: yup
          .string()
          .trim()
          .optional()
          .max(MAX_NUMBER_CHARACTERS, SURNAME_LENGTH)
          .required(SURNAME_REQUIRED)
          .matches(SURNAME_REGEX, SURNAME_NOT_VALID),
        otherwise: yup.string().nullable().notRequired(),
      }),
    [`additionalPolicyHolderDOB`]: yup
      .mixed()
      .when("$isAdditionalPolicyHolder", {
        is: true,
        then: yup
          .date()
          .nullable()
          .typeError(INVALID_DATE)
          .default(undefined)
          .transform(parseDateString)
          .required(DOB_REQUIRED)
          .test("add_dob_min", ADDITIONAL_DOB_MIN_AGE, function (value) {
            return (
              differenceInYears(new Date(), new Date(value)) >=
              ADDITIONAL_HOLDER_MIN_AGE
            );
          })
          .test("add_dob_max", ADDITIONAL_DOB_MAX_AGE, function (value) {
            return (
              differenceInYears(new Date(), new Date(value)) <=
              ADDITIONAL_HOLDER_MAX_AGE
            );
          }),
        otherwise: yup
          .date()
          .nullable()
          .typeError(INVALID_DATE)
          .default(undefined)
          .transform(parseDateString)
          .notRequired(),
      }),
  };
};

// ** Two person schema
export function schemaYourTwoPersonDetailsPersonal() {
  return yup
    .object()
    .shape({
      ...buildPersonalDetailsSchema("personOne"),
      ...buildPersonalDetailsSchema("personTwo"),
    })
    .required();
}

// ** One person schema
export function schemaYourDetailsPersonal() {
  return yup
    .object()
    .shape({
      ...buildPersonalDetailsSchema("personOne"),
    })
    .required();
}

// ----------------------------------------------------------------------
//** Your Details Contact */

//* Email
const EMAIL_REQUIRED = "Email is required";
const EMAIL_REGEX =
  /^[A-Za-z0-9!#$%&*+\-/=?^_{|}]+(\.[A-Za-z0-9!#$%&*+\-/=?^_{|}]+)*@[A-Za-z0-9]+(-[A-Za-z0-9]+)*(\.[A-Za-z0-9]+(-[A-Za-z0-9]+)*)*\.([A-Za-z]{2,}|[A-Za-z]+(-[A-Za-z]+)+)$/;
const EMAIL_NOT_VALID = "Enter a valid email address";
const EMAIL_MAX_LENGTH = 40;
const EMAIL_MAX_LENGTH_MESSAGE = `Email address can only be up to ${EMAIL_MAX_LENGTH} characters`;
const EMAIL_TRIM = "Email address cannot contain spaces";

//* Contact Number
export const CONTACT_NUMBER_REQUIRED = "Mobile number is required";
export const CONTACT_NUMBER_NOT_VALID = "Enter a valid mobile number";
export const CONTACT_NUMBER_STARTING_WITH = "Mobile number must start with 04";
export const CONTACT_NUMBER_TRIM = "Mobile number cannot contain spaces";

//* State
const STATE_REQUIRED = "State is required";

//* Promo Code.
const PROMO_CODE_MAX_LENGTH = 15;
const PROMO_CODE_MAX_LENGTH_MESSAGE = `Promotional code can only be up to ${PROMO_CODE_MAX_LENGTH} characters`;
const PROMO_CODE_ALPHA_NUMERIC =
  "Only allowed alphanumeric characters are allowed";
const PROMO_CODE_REGEX = /^[a-zA-Z0-9]*$/;

//* Questions
const TOBACCO_QUESTION_REQUIRED = "Tobacco question is required";
const INSURANCE_QUESTION_REQUIRED = "Insurance question is required";
const INSURANCE_QUESTION_LIST_REQUIRED = "You must select at least one.";
const CONSENT_QUESTION_REQUIRED = "Consent question is required.";

const buildQuestions = (person) => {
  return {
    [`${person}_tobaccoQuestion`]: yup
      .string()
      .oneOf(["yes", "no"])
      .required(TOBACCO_QUESTION_REQUIRED),
    [`${person}_otherAllianzInsuranceQuestion`]: yup
      .string()
      .oneOf(["yes", "no"])
      .required(INSURANCE_QUESTION_REQUIRED),
    [`${person}_otherAllianzInsuranceList`]: yup
      .array()
      .when([`${person}_otherAllianzInsuranceQuestion`], {
        is: "yes",
        then: yup.array().nullable().required(INSURANCE_QUESTION_LIST_REQUIRED),
        otherwise: yup.array().nullable().notRequired(),
      }),
    // [`${person}_callbackConsent`]: yup
    // 	.string()
    // 	.when("$isConsentCheckRequired", {
    // 		is: false,
    // 		then: yup
    // 			.string()
    // 			.oneOf(["yes", "no"])
    // 			.required(CONSENT_QUESTION_REQUIRED),
    // 		otherwise: yup.string().nullable().notRequired(),
    // 	}),
  };
};

const buildContactDetailsSchema = (person) => {
  return {
    [`${person}_emailAddress`]: yup
      .string()
      .strict(true)
      .trim(EMAIL_TRIM)
      .required(EMAIL_REQUIRED)
      .matches(EMAIL_REGEX, EMAIL_NOT_VALID)
      .max(EMAIL_MAX_LENGTH, EMAIL_MAX_LENGTH_MESSAGE),
    [`${person}_contactNumber`]: yup
      .string()
      .strict(true)
      .trim(CONTACT_NUMBER_TRIM)
      .phone("AU", CONTACT_NUMBER_NOT_VALID)
      .required(CONTACT_NUMBER_REQUIRED)
      .test("mobile_number", CONTACT_NUMBER_STARTING_WITH, function (value) {
        return value?.slice(0, 2) === "04";
      }),
    [`${person}_state`]: yup
      .string()
      .oneOf(["act", "nsw", "vic", "qld", "nt", "sa", "tas", "wa"])
      .required(STATE_REQUIRED),
    [`${person}_promoCode`]: yup
      .string()
      .max(PROMO_CODE_MAX_LENGTH, PROMO_CODE_MAX_LENGTH_MESSAGE)
      .test("is-alphanumeric", PROMO_CODE_ALPHA_NUMERIC, (value) => {
        // Apply validation only if there is a value
        if (value) {
          return PROMO_CODE_REGEX.test(value);
        }
        return true; // If no value, validation passes
      })
      .notRequired(),
    ...buildQuestions("personOne"),
  };
};

// ** Two person schema
export function schemaYourTwoPersonDetailsContact() {
  return yup
    .object()
    .shape({
      ...buildContactDetailsSchema("personOne"),
      ...buildQuestions("personTwo"),
    })
    .required();
}

// ** One person schema
export function schemaYourDetailsContact() {
  return yup
    .object()
    .shape({
      ...buildContactDetailsSchema("personOne"),
    })
    .required();
}

//----------------------------------------------------------------
export function schemaTwoPersonSelectCover() {
  return yup
    .object()
    .shape({
      ...buildSchemaSelectCover("personOne"),
      ...buildSchemaSelectCover("personTwo"),
    })
    .required();
}

export function schemaSelectCover() {
  return yup
    .object()
    .shape({
      ...buildSchemaSelectCover("personOne"),
    })
    .required();
}

//----------------------------------------------------------------

//** Quote Summary -  Based on Select Cover validation */

export function schemaTwoPersonQuoteSummary() {
  return yup.object().shape({
    ...buildQuoteSummarySchema("personOne"),
    ...buildQuoteSummarySchema("personTwo"),
  });
}

export function schemaQuoteSummary() {
  return yup.object().shape({
    ...buildQuoteSummarySchema("personOne"),
  });
}
//----------------------------------------------------------------

//** Opening script - Contact Center */
const openingScriptValidation = (value) => {
  if (value === "no") {
    return false;
  } else {
    return true;
  }
};

export function schemaOpeningScript() {
  return yup.object().shape({
    privacyRecordingQuestion: yup
      .string()
      .oneOf(["yes", "no"])
      .required()
      .test("privacyRecordingQuestionValidation", "", openingScriptValidation),
    additionalSummaryQuestion: yup
      .string()
      .oneOf(["yes", "no"])
      .required()
      .test("additionalSummaryQuestionValidation", "", openingScriptValidation),
    // optOutDirectMarketingQuestion: yup
    //   .string()
    //   .oneOf(["yes", "no"])
    //   .required()
    //   .test(
    //     "optOutDirectMarketingQuestionValidation",
    //     "",
    //     openingScriptValidation
    //   ),
  });
}
const aboutYouTCValidation = (value) => {
  if (value === "no") {
    return false;
  } else {
    return true;
  }
};

export function aboutYouDisclosureSchema() {
  return yup.object().shape({
    aboutYouDisclosureTc: yup
      .string()
      // .oneOf(["yes", "no"])
      .required()
      .test("aboutYouDisclosureTcValid", "", aboutYouTCValidation),
  });
}
